import React, { useCallback, useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { SendRoomMessageBody } from '../../../api/chat'
import { ImageActions } from '../../../actions/image-upload'
import { roomMessageSchema } from '../../../forms/schemas/chat_message'
import { SendBlockedReason } from '../../../generated/api/chat-room'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useUploadMedia } from '../../../hooks/useUploadMedia'
import { theme } from '../../../theme/theme'
import { BlockedCallout } from '../../blocked/BlockedCallout'
import { Button, ButtonVariant } from '../../common/Button'
import { IconChoices } from '../../common/Icon'
import { TextArea } from '../../common/TextArea'
import { MessageBarImages } from '../ChatMessageBar/MessageBarImages'
import { MessageBarUpload } from '../ChatMessageBar/MessageBarUpload'

type RoomMessageForm = InferType<typeof roomMessageSchema>

interface ComposerProps {
  roomId: string
  societyId: string
  canSend: boolean
  sendBlockedReason?: SendBlockedReason
  isSending: boolean
  onSend: (body: SendRoomMessageBody) => Promise<unknown>
}

export const Composer = ({
  roomId,
  societyId,
  canSend,
  sendBlockedReason,
  isSending,
  onSend,
}: ComposerProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const {
    imageUploading,
    imageUploadDisabled: imageLimitReached,
    onImageRemove,
    onImagePaste,
    uploadImage,
    messageImagesDispatch,
    messageImagesState,
  } = useUploadMedia()

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { isValid },
  } = useForm<RoomMessageForm>({
    mode: 'all',
    resolver: yupResolver(roomMessageSchema),
    defaultValues: { message: '', mediaIds: [] },
  })

  const resetForm = useCallback((): void => {
    messageImagesDispatch({ type: ImageActions.RESET })
    reset({ message: '', mediaIds: [] })
  }, [messageImagesDispatch, reset])

  const currentRoom = useRef(roomId)
  useEffect(() => {
    currentRoom.current = roomId
    resetForm()
  }, [roomId, resetForm])
  const isCurrent = (room: string): boolean => currentRoom.current === room

  useEffect(() => {
    setValue(
      'mediaIds',
      messageImagesState.images.flatMap((image) =>
        image.id ? [image.id] : []
      ),
      { shouldValidate: true }
    )
  }, [messageImagesState, setValue])

  const imageUploadDisabled = !canSend || imageLimitReached

  const onSubmit = async (data: RoomMessageForm): Promise<void> => {
    const room = roomId
    try {
      await onSend({ text: data.message ?? '', mediaIds: data.mediaIds ?? [] })
      if (isCurrent(room)) resetForm()
    } catch {
      // RoomPane toasts; keep the draft in the composer.
    }
  }

  const disabled = !canSend || !isValid || imageUploading || isSending

  return (
    <>
      {sendBlockedReason === 'blocked' && (
        <BlockedCallout selectedSocietyId={societyId} />
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-none bg-neutral-98 p-4 md:py-4 md:px-4"
      >
        {!canSend && sendBlockedReason === 'notMember' && (
          <p
            style={theme.textVariants.caption}
            className="mb-2 text-center text-neutral-50"
          >
            {translate('chatRoom.composer.notMember')}
          </p>
        )}
        <div className="flex flex-1 gap-1 self-end">
          <MessageBarUpload
            name="mediaIds"
            control={control}
            imageUploadDisabled={imageUploadDisabled}
            onUploadImage={uploadImage}
          />
          <div className="flex w-full flex-col rounded-xl bg-neutral-92">
            <MessageBarImages
              images={messageImagesState.images}
              onImageRemove={onImageRemove}
            />
            <Controller
              control={control}
              name="message"
              render={({ field: { value, onChange, onBlur } }) => (
                <TextArea
                  className="bg-neutral-92"
                  roundedClassName="rounded-xl"
                  placeholder="Aa"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  onPaste={onImagePaste}
                  rows={1}
                  removeBorders
                  fixedHeight={false}
                  disabled={!canSend}
                  testId="chat-message-input"
                />
              )}
            />
          </div>
          <div className="flex self-end">
            <Button
              type="submit"
              data-testid="chat-send-button"
              disabled={disabled}
              loading={isSending}
              className="h-11 w-11 border-none bg-transparent"
              size="no-padding"
              iconSize={20}
              variant={ButtonVariant.TEXT}
              iconColor="black"
              icon={IconChoices.SEND}
            />
          </div>
        </div>
      </form>
    </>
  )
}
